import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'

import { cards } from '@/store/modules/cards'
import { security } from '@/store/modules/security'
import { notifications } from '@/store/modules/notifications'

import { store } from '@/store'
import { mock } from '@/tools'

Vue.use(VueRouter)

const securityModule = security.context(store)
const notificationsModule = notifications.context(store)

const cardsModule = cards.context(store)

export const mainRoutes = [
  {
    path: 'cards',
    name: 'main/cards',
    component: () => import(/* webpackChunkName: "cards" */ '@/views/main/Cards.vue'),
    meta: {
      template: 'AppTemplate',
      title: 'Управление картами',
      background: false,
      isAuthRequired: true,
    },
  },
  {
    path: 'travelcard',
    name: 'main/travelcard',
    component: () => import(/* webpackChunkName: "cards" */ '@/views/main/TravelCard.vue'),
    meta: {
      template: 'AppTemplate',
      title: 'Транспортный сервис',
      background: false,
      isAuthRequired: true,
    },
  },
]

const routes: Array<RouteConfig> = [
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "login" */ '@/views/Login.vue'),

    meta: {
      template: 'DefaultTemplate',
      title: 'Вход',
      fillHeight: true,
      background: true,
      isAuthRequired: false,
    },
  },
  {
    path: '/welcome',
    name: 'welcome',
    component: () => import(/* webpackChunkName: "welcome" */ '@/views/Welcome.vue'),
    meta: {
      template: 'DefaultTemplate',
      title: 'Добро пожаловать',
      background: false,
      isAuthRequired: true,
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import(/* webpackChunkName: "profile" */ '@/views/ProfileForm.vue'),
    meta: {
      template: 'AppTemplate',
      title: 'Профиль пользователя',
      background: false,
      isAuthRequired: true,
    },
    async beforeEnter(_to, _from, next) {
      await securityModule.actions.getUser().catch(mock)
      next()
    },
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import(/* webpackChunkName: "notifications" */ '@/views/notifications/Notifications.vue'),
    meta: {
      template: 'AppTemplate',
      title: 'Уведомления',
      background: false,
      isAuthRequired: true,
      hideTabs: true,
    },
  },
  {
    path: '/notifications/:notificationId',
    name: 'notifications/detail',
    component: () =>
      import(/* webpackChunkName: "notifications-detail" */ '@/views/notifications/NotificationDetail.vue'),
    meta: {
      template: 'AppTemplate',
      title: 'Просмотр уведомления',
      background: false,
      isAuthRequired: true,
      hideTabs: true,
    },
  },
  {
    path: 'loyalty',
    name: 'loyalty',
    component: () => import(/* webpackChunkName: "loyalty" */ '@/views/loyalty/Loyalty.vue'),
    redirect: { name: 'loyalty/certificates' },
    meta: {
      template: 'AppTemplate',
      background: false,
      isAuthRequired: true,
    },
    children: [
      {
        path: '/certificates',
        name: 'loyalty/certificates',
        component: () => import(/* webpackChunkName: "certificates" */ '@/views/loyalty/Certificates.vue'),
        meta: {
          template: 'AppTemplate',
          title: 'Мои сертификаты',
          background: false,
          isAuthRequired: true,
          hideTabs: true,
        },
      },
      {
        path: '/bonuses',
        name: 'loyalty/bonuses',
        component: () => import(/* webpackChunkName: "bonuses" */ '@/views/loyalty/Bonuses.vue'),
        meta: {
          template: 'AppTemplate',
          title: 'Бонусный счет',
          background: false,
          isAuthRequired: true,
          hideTabs: true,
        },
      },
    ],
  },

  {
    path: '/logout',
    name: 'logout',
    async beforeEnter(_to, _from, next) {
      await notificationsModule.actions.enableCheckNotificationsLoop(false)
      await securityModule.actions.logout()
      next({ name: 'login' })
    },
    meta: {
      template: 'AppTemplate',
      title: 'Выход...',
      background: false,
      isAuthRequired: false,
    },
  },
  {
    path: '/connect',
    name: 'connect',
    component: () => import(/* webpackChunkName: "connect" */ '@/views/ConnectCardForm.vue'),
    meta: {
      template: 'AppTemplate',
      title: 'Подключить новую карту',
      fillHeight: true,
      background: false,
      isAuthRequired: true,
    },
    async beforeEnter(to, from, next) {
      if (!(await securityModule.actions.checkCabinetAccess().catch(mock))) {
        to.meta.template = 'DefaultTemplate'
        next()
      } else if (await cardsModule.actions.checkCardsActive().catch(mock)) {
        to.meta.template = 'AppTemplate'
        next()
      } else if (from.name === null) next({ name: 'main' })
      else next(false)
    },
  },
  {
    path: '/',
    name: 'main',
    component: () => import(/* webpackChunkName: "main" */ '@/views/main/Main.vue'),
    meta: {
      template: 'AppTemplate',
      background: false,
      isAuthRequired: true,
    },
    redirect: { name: 'main/cards' },
    children: mainRoutes,
    async beforeEnter(to, _from, next) {
      if (await securityModule.actions.checkCabinetAccess().catch(mock)) {
        to.name === 'main' ? await router.push({ name: 'main/cards', replace: true }) : next()
      } else await router.push({ name: 'welcome', replace: true })
    },
  },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.PUBLIC_PATH,
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition || { x: 0, y: 0 }
  },
})

router.beforeEach(async (to, _from, next) => {
  try {
    if (to.meta.isAuthRequired && !securityModule.state.user) {
      await securityModule.actions.checkCabinetAccess()
      await securityModule.actions.getUser()
      if (securityModule.getters.isUser) next()
      else next({ name: 'login' })
    } else next()
  } catch (error) {
    next({ name: 'login' })
    mock(error)
  }
})

router.afterEach((to, _from) => {
  document.title = to.meta.title || process.env.VUE_APP_TITLE
})

export default router
